/** @jsx jsx */
import React, { useState } from 'react';
import { jsx } from '@emotion/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@material-ui/lab';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';

import { postUserChallengePhotoResults } from 'actions/challenge.actions';
import { useImgSrc } from 'core/utils/use-img-src.hook';
import {
  ChallengeResultType,
  ChallengePhaseType,
  UserPhotoTypes,
  LoadingStatusType,
  ICustomTheme,
} from 'interfaces';
import { Photo } from 'components';
import { CHALLENGE_PHASE, LOADING_STATUS } from 'constants/index';
import AddPhotoModal from '../AddPhotoModal';
import FullDescription from './FullDescription';
import * as S from './styles';

const { cssAlignContent } = S;

type PhotoContestResultsType = {
  challengeId: number;
  currentPhase: ChallengePhaseType | null;
  currentUserResult?: ChallengeResultType;
  resultsLoadingStatus?: LoadingStatusType;
  totalParticipatingUsers: number | null;
};

const PhotoContestResults = ({
  challengeId,
  currentPhase,
  currentUserResult,
  resultsLoadingStatus,
  totalParticipatingUsers,
}: PhotoContestResultsType): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [userPhoto, setUserPhoto] = useState<UserPhotoTypes>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const {
    id: resultId,
    image: currentUserPhoto,
    voters,
    status,
  } = currentUserResult || {};
  const currentUserVotes = voters?.length || null;
  const { fullUri, src } = useImgSrc(currentUserPhoto?.id) || {};

  const hasChallengeStarted =
    currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE ||
    currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;
  const hasChallengeEnded = currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;

  const submitPhoto = () => {
    if (userPhoto && Object.entries(userPhoto).length !== 0) {
      const { raw: photo } = userPhoto;
      dispatch(
        postUserChallengePhotoResults.request({
          challengeId,
          photo,
          status,
          resultId,
        }),
      );
    }
  };

  return (
    <React.Fragment>
      {userPhoto && (
        <AddPhotoModal
          onSubmit={submitPhoto}
          open={isModalOpen}
          setOpenModal={setIsModalOpen}
          resetUserPhoto={() => setUserPhoto(null)}
          userPhoto={userPhoto}
          challengeId={challengeId}
        />
      )}
      <S.Card>
        <S.Title
          variant={isDownSm ? 'h5' : 'h3'}
          align={isDownSm ? 'center' : 'left'}
          paragraph
        >
          {t('photo_contest.results.title')}
        </S.Title>

        <Grid
          container
          alignItems="center"
          spacing={3}
          direction={isDownSm ? 'column-reverse' : 'row'}
        >
          <Grid item xs={12} md={3} css={cssAlignContent}>
            <S.Wrapper>
              {resultsLoadingStatus === LOADING_STATUS.PENDING ? (
                <Skeleton variant="circle" height={235} width={235} />
              ) : (
                <Photo
                  isCurrentUser
                  photoSrc={src}
                  setOpenModal={() => setIsModalOpen(true)}
                  setUserPhoto={setUserPhoto}
                  status={status}
                  userPhoto={userPhoto}
                  disabled={hasChallengeEnded || !hasChallengeStarted}
                />
              )}
            </S.Wrapper>
          </Grid>
          <FullDescription
            currentUserVotes={currentUserVotes}
            fullUri={fullUri}
            hasChallengeEnded={hasChallengeEnded}
            hasChallengeStarted={hasChallengeStarted}
            resultsLoadingStatus={resultsLoadingStatus}
            status={status}
            totalParticipatingUsers={totalParticipatingUsers}
          />
        </Grid>
      </S.Card>
    </React.Fragment>
  );
};

export default PhotoContestResults;
